/**
 * Validates and normalizes job field values to ensure they match allowed enum values
 */

import {
  filterBenefitsToCanonicalEnum,
  normalizeJobTypeForLocale,
  normalizeRemotenessForLocale,
  normalizeStandaloneRemoteInLocationArrays,
} from './processor-output-fields';
import { normalizeJobFieldLabelsOnJob } from '../text/normalize-job-field-label';
import {
  LANGUAGE_NAME_SETS,
  LANGUAGE_NAME_TO_TRIPLE,
  findFuzzyLanguageMatch,
  type LanguageLocale,
} from './language-names';

export interface JobWithValidatableFields {
  location_en?: string[];
  location_sv?: string[];
  location_fi?: string[];
  benefits_en?: string[];
  benefits_sv?: string[];
  benefits_fi?: string[];
  remoteness_en?: string;
  remoteness_sv?: string;
  remoteness_fi?: string;
  jobType_en?: string;
  jobType_sv?: string;
  jobType_fi?: string;
  normalisedRoleLevel_en?: string;
  normalisedRoleLevel_sv?: string;
  normalisedRoleLevel_fi?: string;
  peopleManagementTier_en?: string;
  peopleManagementTier_sv?: string;
  peopleManagementTier_fi?: string;
  requiredLanguages_en?: string[];
  requiredLanguages_sv?: string[];
  requiredLanguages_fi?: string[];
  optionalLanguages_en?: string[];
  optionalLanguages_sv?: string[];
  optionalLanguages_fi?: string[];
  educationLevel_en?: string;
  educationLevel_sv?: string;
  educationLevel_fi?: string;
  [key: string]: any;
}

// Valid education level values
export const VALID_EDUCATION_LEVELS = {
  en: ['No degree required', 'High school', 'Bachelor', 'Masters', 'Doctorate', 'Apprenticeship', 'Diploma'],
  sv: ['Ingen utbildning krävs', 'Gymnasieutbildning', 'Kandidat', 'Magister', 'Doktorsexamen', 'Läroavtalsutbildning', 'Diplom'],
  fi: ['Ei koulutusta vaadita', 'Toisen asteen koulutus', 'Alempi korkeakoulututkinto', 'Ylempi korkeakoulututkinto', 'Tieteellinen jatkotutkinto', 'Oppisopimuskoulutus', 'Diplomi'],
} as const;

// Common language names (not exhaustive, but covers most cases). Kept as a flat union for
// backward compatibility; prefer LANGUAGE_NAME_SETS (locale-keyed, from language-names.ts) for
// anything that needs to validate/normalize a value against ONE specific locale's own vocabulary:
// this flat set can't tell "Finnish" (valid in en) from a leaked English value inside a sv field.
export const VALID_LANGUAGE_NAMES = new Set<string>([
  ...LANGUAGE_NAME_SETS.en,
  ...LANGUAGE_NAME_SETS.fi,
  ...LANGUAGE_NAME_SETS.sv,
]);

// Valid normalised role level values
export const VALID_ROLE_LEVELS = {
  en: ['Intern', 'Junior', 'Mid-Level', 'Senior', 'Expert', 'Manager', 'Director', 'Executive'],
  sv: ['Praktikant', 'Junior', 'Medelnivå', 'Senior', 'Expert', 'Chef', 'Direktör', 'Företagsledare'],
  fi: ['Harjoittelija', 'Juniori', 'Keskitaso', 'Senior-taso', 'Asiantuntija', 'Esimies', 'Johtaja', 'Ylin johto'],
} as const;

// Cross-locale mapping: English → Finnish/Swedish for role levels
const ROLE_LEVEL_EN_TO_FI: Record<string, string> = {
  'Intern': 'Harjoittelija',
  'Junior': 'Juniori',
  'Mid-Level': 'Keskitaso',
  'Senior': 'Senior-taso',
  'Expert': 'Asiantuntija',
  'Manager': 'Esimies',
  'Director': 'Johtaja',
  'Executive': 'Ylin johto',
};
const ROLE_LEVEL_EN_TO_SV: Record<string, string> = {
  'Intern': 'Praktikant',
  'Junior': 'Junior',
  'Mid-Level': 'Medelnivå',
  'Senior': 'Senior',
  'Expert': 'Expert',
  'Manager': 'Chef',
  'Director': 'Direktör',
  'Executive': 'Företagsledare',
};

// Valid people management tier values
export const VALID_PEOPLE_MANAGEMENT = {
  en: ['No People Management', 'People Manager', 'Manager of Managers'],
  sv: ['Inget personalansvar', 'Chef med personalansvar', 'Chef över chefer'],
  fi: ['Ei esimiestehtäviä', 'Esimies', 'Esimiesten esimies'],
} as const;

// Cross-locale mapping: English → Finnish/Swedish for people management
const PEOPLE_MGMT_EN_TO_FI: Record<string, string> = {
  'No People Management': 'Ei esimiestehtäviä',
  'People Manager': 'Esimies',
  'Manager of Managers': 'Esimiesten esimies',
};
const PEOPLE_MGMT_EN_TO_SV: Record<string, string> = {
  'No People Management': 'Inget personalansvar',
  'People Manager': 'Chef med personalansvar',
  'Manager of Managers': 'Chef över chefer',
};

function capitalizeWordsForFacet(s: string): string {
  return s
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => (w.length ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : w))
    .join(' ');
}

/** Maps lowercase URL facet values back to Algolia strings (paired with lowercased query params in URLs). */
export function resolveRoleLevelFromUrl(value: string, locale: 'en' | 'fi' | 'sv'): string {
  const trimmed = value.trim();
  if (!trimmed) return trimmed;
  const lower = trimmed.toLowerCase();
  for (const v of VALID_ROLE_LEVELS[locale]) {
    if (v.toLowerCase() === lower) return v;
  }
  const enMatch = VALID_ROLE_LEVELS.en.find((v) => v.toLowerCase() === lower);
  if (enMatch) {
    if (locale === 'fi' && ROLE_LEVEL_EN_TO_FI[enMatch]) return ROLE_LEVEL_EN_TO_FI[enMatch];
    if (locale === 'sv' && ROLE_LEVEL_EN_TO_SV[enMatch]) return ROLE_LEVEL_EN_TO_SV[enMatch];
    if (locale === 'en') return enMatch;
  }
  return capitalizeWordsForFacet(trimmed);
}

export function resolveEducationLevelFromUrl(value: string, locale: 'en' | 'fi' | 'sv'): string {
  const trimmed = value.trim();
  if (!trimmed) return trimmed;
  const lower = trimmed.toLowerCase();
  for (const v of VALID_EDUCATION_LEVELS[locale]) {
    if (v.toLowerCase() === lower) return v;
  }
  return capitalizeWordsForFacet(trimmed);
}

export function resolvePeopleManagementFromUrl(value: string, locale: 'en' | 'fi' | 'sv'): string {
  const trimmed = value.trim();
  if (!trimmed) return trimmed;
  const lower = trimmed.toLowerCase();
  for (const v of VALID_PEOPLE_MANAGEMENT[locale]) {
    if (v.toLowerCase() === lower) return v;
  }
  const enMatch = VALID_PEOPLE_MANAGEMENT.en.find((v) => v.toLowerCase() === lower);
  if (enMatch) {
    if (locale === 'fi' && PEOPLE_MGMT_EN_TO_FI[enMatch]) return PEOPLE_MGMT_EN_TO_FI[enMatch];
    if (locale === 'sv' && PEOPLE_MGMT_EN_TO_SV[enMatch]) return PEOPLE_MGMT_EN_TO_SV[enMatch];
    if (locale === 'en') return enMatch;
  }
  return capitalizeWordsForFacet(trimmed);
}

export function resolveLanguageNameFromUrl(value: string, locale: LanguageLocale = 'en'): string {
  const trimmed = value.trim();
  if (!trimmed) return trimmed;
  const triple = LANGUAGE_NAME_TO_TRIPLE.get(trimmed.toLowerCase());
  if (triple) return triple[locale];
  return capitalizeWordsForFacet(trimmed);
}

/**
 * Normalizes a role level value. If the value is an English enum in a non-English
 * locale field, maps it to the correct locale equivalent.
 */
function normalizeRoleLevel(value: string | undefined, locale: 'en' | 'sv' | 'fi'): string | undefined {
  if (!value || typeof value !== 'string') return undefined;

  const trimmed = value.trim();
  if (!trimmed) return undefined;

  const validValues = VALID_ROLE_LEVELS[locale];

  if (validValues.includes(trimmed as any)) return trimmed;

  // Cross-locale recovery: if the value is an English enum, map to target locale
  if (locale === 'fi' && ROLE_LEVEL_EN_TO_FI[trimmed]) return ROLE_LEVEL_EN_TO_FI[trimmed];
  if (locale === 'sv' && ROLE_LEVEL_EN_TO_SV[trimmed]) return ROLE_LEVEL_EN_TO_SV[trimmed];

  return undefined;
}

/**
 * Normalizes a people management tier value. If the value is an English enum in a
 * non-English locale field, maps it to the correct locale equivalent.
 */
function normalizePeopleManagement(value: string | undefined, locale: 'en' | 'sv' | 'fi'): string | undefined {
  if (!value || typeof value !== 'string') return undefined;

  const trimmed = value.trim();
  if (!trimmed) return undefined;

  const validValues = VALID_PEOPLE_MANAGEMENT[locale];

  if (validValues.includes(trimmed as any)) return trimmed;

  // Cross-locale recovery: if the value is an English enum, map to target locale
  if (locale === 'fi' && PEOPLE_MGMT_EN_TO_FI[trimmed]) return PEOPLE_MGMT_EN_TO_FI[trimmed];
  if (locale === 'sv' && PEOPLE_MGMT_EN_TO_SV[trimmed]) return PEOPLE_MGMT_EN_TO_SV[trimmed];

  return undefined;
}

// Proficiency/filler words that sometimes wrap a language name in extracted text (e.g.
// "Fluent Finnish", "Native English speaker", "Knowledge of German (B2)") — stripped so the
// underlying language can still resolve to a VALID_LANGUAGE_NAMES entry instead of being
// rejected outright. Deliberately does NOT touch phrases like "Scandinavian languages" or
// "EU languages" — those stay rejected by the invalidPatterns check in isValidLanguageName.
const LANGUAGE_QUALIFIER_WORDS = new Set([
  'fluent', 'native', 'basic', 'intermediate', 'advanced', 'professional', 'working',
  'conversational', 'beginner', 'elementary', 'proficient', 'good', 'excellent', 'speaker',
  'speakers', 'mother', 'tongue', 'level', 'proficiency', 'knowledge', 'skills', 'of', 'in',
]);

function stripLanguageQualifiers(value: string): string {
  const withoutParens = value.replace(/\([^)]*\)/g, ' '); // drop "(C1)", "(native)"
  const core = withoutParens
    .split(/\s+/)
    .filter(Boolean)
    .filter((w) => !LANGUAGE_QUALIFIER_WORDS.has(w.toLowerCase()));
  return core.join(' ').trim();
}

// Common typos and variations mapping (only for actual typos, not case variations, not
// cross-locale leaks: those are handled by LANGUAGE_NAME_TO_TRIPLE below).
// Key: lowercase version of the typo. Value: correct language name.
const LANGUAGE_TYPOS: Record<string, string> = {
  'finnska': 'Finska', // Common typo: double 'n' instead of single 'n'
};

/**
 * Normalizes a language name for a SPECIFIC locale: fixes casing, typos, and proficiency-word
 * wrappers ("Fluent Finnish" -> "Finnish"), and, unlike the pre-2026-09 version of this
 * function, translates a value that's valid but in the WRONG locale (e.g. "Finnish" or "Suomi"
 * found inside a requiredLanguages_sv array becomes "Finska", not passed through untranslated).
 * Also corrects a malformed grammatical form of a real language name that's one edit away from
 * the target locale's own spelling (e.g. Swedish "Finskt" -> "Finska").
 */
export function normalizeLanguageName(language: string, locale: LanguageLocale): string {
  if (!language || typeof language !== 'string') return language;

  const trimmed = language.trim();
  if (!trimmed) return language;

  const ownSet = LANGUAGE_NAME_SETS[locale];

  // Already correct for this locale, verbatim.
  if (ownSet.has(trimmed)) return trimmed;

  // Case-insensitive match within this locale's own vocabulary.
  const lowerTrimmed = trimmed.toLowerCase();
  for (const validLang of ownSet) {
    if (validLang.toLowerCase() === lowerTrimmed) return validLang;
  }

  // Cross-locale recovery: the value is a real language name, just in the wrong locale (or
  // already correct in every locale, e.g. "Hindi"): translate it to the target locale.
  const triple = LANGUAGE_NAME_TO_TRIPLE.get(lowerTrimmed);
  if (triple) return triple[locale];

  // Known typos, e.g. "Finnska" (double n).
  if (LANGUAGE_TYPOS[lowerTrimmed]) return LANGUAGE_TYPOS[lowerTrimmed];

  // Try stripping proficiency/filler qualifiers and re-matching (e.g. "Fluent Finnish" -> "Finnish").
  const stripped = stripLanguageQualifiers(trimmed);
  if (stripped && stripped !== trimmed) {
    const strippedLower = stripped.toLowerCase();
    if (ownSet.has(stripped)) return stripped;
    for (const validLang of ownSet) {
      if (validLang.toLowerCase() === strippedLower) return validLang;
    }
    const strippedTriple = LANGUAGE_NAME_TO_TRIPLE.get(strippedLower);
    if (strippedTriple) return strippedTriple[locale];
  }

  // Malformed/near-miss form of a real language name in this locale (e.g. adjectival "Finskt"
  // instead of the noun "Finska"): close enough (edit distance <= 1) to trust the correction.
  const fuzzy = findFuzzyLanguageMatch(trimmed, locale);
  if (fuzzy) return fuzzy;

  // If no normalization found, return original (will be validated later)
  return trimmed;
}

/**
 * Validates that a language name is a valid language for the given locale (not a phrase like
 * "Scandinavian languages", and not a malformed/wrong-locale form of a real language name that
 * normalizeLanguageName should have already caught (that check runs first here too, so a caller
 * that skips normalization doesn't silently accept e.g. "Finskt").
 */
export function isValidLanguageName(language: string, locale: LanguageLocale): boolean {
  if (!language || typeof language !== 'string') return false;

  const trimmed = language.trim();
  if (!trimmed) return false;

  const ownSet = LANGUAGE_NAME_SETS[locale];
  const lowerTrimmed = trimmed.toLowerCase();

  // Valid for this exact locale.
  if (ownSet.has(trimmed)) return true;
  for (const validLang of ownSet) {
    if (validLang.toLowerCase() === lowerTrimmed) return true;
  }

  // A near-miss (edit distance <= 1) of a known language name in ANY locale means this is a
  // malformed/mistranslated form of a real language, not a genuinely novel one: reject it here
  // so it doesn't fall through to the permissive single-word rule below. normalizeLanguageName
  // is what should have already corrected this; this is the backstop for a caller that didn't
  // normalize first.
  for (const otherLocale of ['en', 'fi', 'sv'] as const) {
    if (otherLocale === locale) continue;
    if (findFuzzyLanguageMatch(trimmed, otherLocale)) return false;
  }
  // One edit away, but still didn't match above: treat as malformed rather than valid.
  if (findFuzzyLanguageMatch(trimmed, locale)) return false;

  // Reject common invalid patterns
  const invalidPatterns = [
    /languages?$/i,  // "Scandinavian languages", "European languages"
    /language groups?/i,
    /dialects?/i,
    /varieties?/i,
  ];

  for (const pattern of invalidPatterns) {
    if (pattern.test(trimmed)) {
      return false;
    }
  }

  // If it's a single word and looks like a language name, allow it
  // (covers genuinely rare languages not in our canonical table)
  if (/^[A-ZÄÖÅ][a-zäöå]+$/.test(trimmed)) {
    return true;
  }

  return false;
}

/**
 * Normalizes an education level value to a valid enum value
 */
function normalizeEducationLevel(value: string | undefined, locale: 'en' | 'sv' | 'fi'): string | undefined {
  if (!value || typeof value !== 'string') return undefined;
  
  const validValues = VALID_EDUCATION_LEVELS[locale];
  const lowerValue = value.toLowerCase().trim();
  
  // Direct match
  if (validValues.includes(value as any)) {
    return value;
  }
  
  // Case-insensitive match
  for (const validValue of validValues) {
    if (validValue.toLowerCase() === lowerValue) {
      return validValue;
    }
  }
  
  // Common variations mapping
  const variations: Record<string, Record<string, string>> = {
    en: {
      'none required': 'No degree required',
      'no education required': 'No degree required',
      'no degree required': 'No degree required',
      'high school': 'High school',
      'bachelor': 'Bachelor',
      "bachelor's": 'Bachelor',
      "bachelor's degree": 'Bachelor',
      'masters': 'Masters',
      "master's": 'Masters',
      "master's degree": 'Masters',
      'doctorate': 'Doctorate',
      'phd': 'Doctorate',
      'apprenticeship': 'Apprenticeship',
      'diploma': 'Diploma',
    },
    sv: {
      'ingen krävs': 'Ingen utbildning krävs',
      'ingen utbildning krävs': 'Ingen utbildning krävs',
      'gymnasieutbildning': 'Gymnasieutbildning',
      'kandidat': 'Kandidat',
      'masters': 'Magister',
      'doktorand': 'Doktorsexamen',
      'lärlingsutbildning': 'Läroavtalsutbildning',
      'diplom': 'Diplom',
    },
    fi: {
      'ei vaadita': 'Ei koulutusta vaadita',
      'ei koulutusta vaadita': 'Ei koulutusta vaadita',
      'toisen asteen koulutus': 'Toisen asteen koulutus',
      'alempi korkeakoulututkinto': 'Alempi korkeakoulututkinto',
      'ylempi korkeakoulututkinto': 'Ylempi korkeakoulututkinto',
      'tieteellinen jatkotutkinto': 'Tieteellinen jatkotutkinto',
      'oppisopimuskoulutus': 'Oppisopimuskoulutus',
      'diplomi': 'Diplomi',
    },
  };
  
  const normalized = variations[locale]?.[lowerValue];
  if (normalized) {
    return normalized;
  }
  
  // If no match found, return undefined (invalid value)
  return undefined;
}

/**
 * Validates and normalizes all job fields
 */
export function validateJobFields(job: JobWithValidatableFields): void {
  normalizeStandaloneRemoteInLocationArrays(job);
  filterBenefitsToCanonicalEnum(job);

  // Validate remoteness fields
  if (job.remoteness_en !== undefined) {
    job.remoteness_en = normalizeRemotenessForLocale(job.remoteness_en, 'en') || undefined;
  }
  if (job.remoteness_sv !== undefined) {
    job.remoteness_sv = normalizeRemotenessForLocale(job.remoteness_sv, 'sv') || undefined;
  }
  if (job.remoteness_fi !== undefined) {
    job.remoteness_fi = normalizeRemotenessForLocale(job.remoteness_fi, 'fi') || undefined;
  }

  // Validate job type fields
  if (job.jobType_en !== undefined) {
    job.jobType_en = normalizeJobTypeForLocale(job.jobType_en, 'en') || undefined;
  }
  if (job.jobType_sv !== undefined) {
    job.jobType_sv = normalizeJobTypeForLocale(job.jobType_sv, 'sv') || undefined;
  }
  if (job.jobType_fi !== undefined) {
    job.jobType_fi = normalizeJobTypeForLocale(job.jobType_fi, 'fi') || undefined;
  }
  
  // Validate normalised role level fields
  if (job.normalisedRoleLevel_en !== undefined) {
    job.normalisedRoleLevel_en = normalizeRoleLevel(job.normalisedRoleLevel_en, 'en') || undefined;
  }
  if (job.normalisedRoleLevel_sv !== undefined) {
    job.normalisedRoleLevel_sv = normalizeRoleLevel(job.normalisedRoleLevel_sv, 'sv') || undefined;
  }
  if (job.normalisedRoleLevel_fi !== undefined) {
    job.normalisedRoleLevel_fi = normalizeRoleLevel(job.normalisedRoleLevel_fi, 'fi') || undefined;
  }

  // Validate people management tier fields
  if (job.peopleManagementTier_en !== undefined) {
    job.peopleManagementTier_en = normalizePeopleManagement(job.peopleManagementTier_en, 'en') || undefined;
  }
  if (job.peopleManagementTier_sv !== undefined) {
    job.peopleManagementTier_sv = normalizePeopleManagement(job.peopleManagementTier_sv, 'sv') || undefined;
  }
  if (job.peopleManagementTier_fi !== undefined) {
    job.peopleManagementTier_fi = normalizePeopleManagement(job.peopleManagementTier_fi, 'fi') || undefined;
  }

  // Validate and normalize language fields (normalize first, then remove invalid entries).
  // Each field is validated against ITS OWN locale's vocabulary: a value normalizes/translates
  // to the locale the field is actually for, instead of being accepted verbatim in whichever
  // locale the model happened to generate it in (see normalizeLanguageName's docstring).
  const languageFields: { field: 'requiredLanguages_en' | 'requiredLanguages_sv' | 'requiredLanguages_fi'
    | 'optionalLanguages_en' | 'optionalLanguages_sv' | 'optionalLanguages_fi'; locale: LanguageLocale }[] = [
    { field: 'requiredLanguages_en', locale: 'en' },
    { field: 'requiredLanguages_sv', locale: 'sv' },
    { field: 'requiredLanguages_fi', locale: 'fi' },
    { field: 'optionalLanguages_en', locale: 'en' },
    { field: 'optionalLanguages_sv', locale: 'sv' },
    { field: 'optionalLanguages_fi', locale: 'fi' },
  ];

  for (const { field, locale } of languageFields) {
    const raw = job[field];
    if (typeof raw === 'string' && raw.trim()) {
      job[field] = [raw.trim()] as any;
    } else if (raw != null && !Array.isArray(raw)) {
      job[field] = [] as any;
    } else if (raw === null) {
      job[field] = [] as any;
    }
    // undefined → field was absent; leave it untouched
    if (job[field] && Array.isArray(job[field])) {
      // First normalize each language name for this field's own locale, then filter out invalid ones
      job[field] = (job[field] as string[])
        .map((lang: string) => normalizeLanguageName(lang, locale))
        .filter((lang: string) => isValidLanguageName(lang, locale));
    }
  }
  
  // Validate education level fields
  if (job.educationLevel_en !== undefined) {
    job.educationLevel_en = normalizeEducationLevel(job.educationLevel_en, 'en') || undefined;
  }
  if (job.educationLevel_sv !== undefined) {
    job.educationLevel_sv = normalizeEducationLevel(job.educationLevel_sv, 'sv') || undefined;
  }
  if (job.educationLevel_fi !== undefined) {
    job.educationLevel_fi = normalizeEducationLevel(job.educationLevel_fi, 'fi') || undefined;
  }

  normalizeJobFieldLabelsOnJob(job);
}
